import React, { useState, useEffect } from "react";
import { useData } from "../hooks/useData.js";
import { apiFetch } from "../api/index.js";
import { useNotification } from "../context/NotificationContext.jsx";

import {
  PageHeader,
  Button,
  LoadingSpinner,
  Modal,
  ConfirmModal,
  FormInput
} from "../components/Common.jsx";
import { DynamicIcon } from "../components/icons.jsx";

// NEW TAB COMPONENTS
import FeaturesTab from "./plans/FeaturesTab.jsx";
import SupportTiersTab from "./plans/SupportTiersTab.jsx";



const PlanForm = ({ plan, onSave, onCancel, availableFeatures = [] }) => {
  const [formData, setFormData] = useState({
    name: plan?.name || "",
    price: plan?.price || 0,
    monthly_credits: plan?.monthly_credits || 0,
    max_users: plan?.max_users || null,
    features: plan?.features || {
      components: [],
      environments: [],
      support: "Standard Support",
    }
  });

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "number" ? (value === "" ? "" : parseInt(value, 10)) : value
    }));
  };

  // Fallback if API fails or is empty, to prevent empty UI
  const displayFeatures = availableFeatures.length > 0
    ? availableFeatures
    : [
      { code: "dashboard_access", name: "Dashboard - View" },
      { code: "dashboard_studio", name: "Dashboard Studio" },
      { code: "data_models", name: "Data Models" },
      { code: "data_studio", name: "Data Studio (ETL Flows)" },
      { code: "ml_engine", name: "ML Engine (Runtime)" },
      { code: "ml_studio", name: "ML Studio" },
      { code: "genai_studio", name: "GenAI Studio" },
      { code: "workflow_studio", name: "Workflow Studio" },
      { code: "app_marketplace", name: "App Marketplace" },
    ];

  const handleFeatureChange = (category, value) => {
    setFormData((prev) => {
      const current = new Set(prev.features[category] || []);
      const isAdding = !current.has(value);

      if (isAdding) {
        current.add(value);
        // Dependencies: Selecting Child -> Selects Parent
        if (value === "dashboard_studio") current.add("dashboard_access");
        if (value === "ml_studio") current.add("ml_engine");
      } else {
        current.delete(value);
        // Reverse Dependencies: Deselecting Parent -> Deselects Child
        if (value === "dashboard_access") current.delete("dashboard_studio");
        if (value === "ml_engine") current.delete("ml_studio");
      }

      return {
        ...prev,
        features: { ...prev.features, [category]: Array.from(current) }
      };
    });
  };

  const handleProvisionDbChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      features: { ...prev.features, provisionDatabase: e.target.checked }
    }));
  };

  const handleSupportChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      features: { ...prev.features, support: e.target.value }
    }));
  };

  const ENVIRONMENTS = ["Shared", "High-Performance", "Dedicated"];
  const SUPPORT_LEVELS = ["Standard Support", "Priority Support", "Dedicated Account Manager"];

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave(formData);
      }}
      className="space-y-4"
    >
      <FormInput label="Plan Name" name="name" value={formData.name} onChange={handleChange} required />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormInput label="Price ($/mo)" type="number" name="price" value={formData.price} onChange={handleChange} />
        <FormInput label="Credits (Monthly)" type="number" name="monthly_credits" value={formData.monthly_credits} onChange={handleChange} />
        <FormInput label="Max Users (Concurrent)" type="number" name="max_users" value={formData.max_users ?? ""} onChange={handleChange} placeholder="Unlimited if blank" />
      </div>

      <div>
        <h4 className="font-semibold mb-2">Components (Dynamic)</h4>
        <div className="grid grid-cols-2 gap-2">
          {displayFeatures.map((feat, idx) => {
            const val = feat.code || feat.featureName || feat.name; // Prefer code
            const label = feat.name || feat.featureName || val;
            return (
              <label key={idx} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.features.components.includes(val)}
                  onChange={() => handleFeatureChange("components", val)}
                />
                <span>{label}</span>
              </label>
            );
          })}
        </div>
      </div>

      <div>
        <h4 className="font-semibold mb-2">Environments</h4>
        <div className="grid grid-cols-2 gap-2">
          {ENVIRONMENTS.map((e) => (
            <label key={e} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.features.environments.includes(e)}
                onChange={() => handleFeatureChange("environments", e)}
              />
              <span>{e}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <h4 className="font-semibold mb-2">Support Level</h4>
        <select className="w-full p-2 border rounded" value={formData.features.support} onChange={handleSupportChange}>
          {SUPPORT_LEVELS.map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
      </div>


      <div className="flex justify-end gap-3 pt-3">
        <Button variant="secondary" onClick={onCancel} withShadow>Cancel</Button>
        <Button type="submit">{plan ? "Save" : "Create"}</Button>
      </div>
    </form>
  );
};


const PlansPage = ({ token }) => {
  const { data: plans, isLoading, error, refresh } = useData("/plans", token);
  const { addNotification } = useNotification();

  const [tab, setTab] = useState("plans"); // plans | features | support
  const [viewMode, setViewMode] = useState("list"); // grid | list

  // Modals
  const [planModal, setPlanModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [deletePlan, setDeletePlan] = useState(null);

  // Fetch available features for the form
  const [availableFeatures, setAvailableFeatures] = useState([]);
  useEffect(() => {
    apiFetch("/plans/features", token)
      .then(res => setAvailableFeatures(Array.isArray(res) ? res : []))
      .catch(err => console.error("Failed to load features for form:", err));
  }, [token]);

  const handleSavePlan = async (data) => {
    const method = editingPlan ? "PUT" : "POST";
    const endpoint = editingPlan ? `/plans/${editingPlan.id}` : "/plans";

    try {
      await apiFetch(endpoint, token, { method, body: JSON.stringify(data) });
      addNotification("Plan saved.");
      refresh();
    } catch (err) {
      addNotification(err?.message || "Failed to save plan", "error");
    } finally {
      setPlanModal(false);
      setEditingPlan(null);
    }
  };

  const handleDeletePlan = async (p) => {
    try {
      await apiFetch(`/plans/${p.id}`, token, { method: "DELETE" });
      addNotification("Plan deleted.");
      refresh();
    } catch (err) {
      addNotification(err?.message || "Failed to delete plan", "error");
    } finally {
      setDeletePlan(null);
    }
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) return <div className="text-red-600">Error loading plans...</div>;


  return (
    <div>
      <PageHeader title="Plans & Billing">
        {tab === "plans" && (
          <Button onClick={() => { setEditingPlan(null); setPlanModal(true); }}>
            <DynamicIcon name="PlusCircle" className="mr-2" /> Create Plan
          </Button>
        )}
      </PageHeader>

      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
        {/* Tabs */}
        <div className="flex gap-8 text-lg font-semibold border-b sm:border-0 w-full sm:w-auto">
          {["plans", "features", "support"].map((t) => (
            <button
              key={t}
              className={`pb-3 px-1 ${tab === t ? "text-primary border-b-2 border-primary" : "text-gray-500 hover:text-gray-700"}`}
              onClick={() => setTab(t)}
            >
              {t === "plans" ? "Plans" : t === "features" ? "Features" : "Support Tiers"}
            </button>
          ))}
        </div>

        {/* View Toggle (Only for Plans tab) */}
        {tab === "plans" && (
          <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1 hidden md:flex">
            <button
              onClick={() => setViewMode("grid")}
              className={`p-2 rounded-md transition-all ${viewMode === "grid" ? "bg-white dark:bg-gray-600 shadow-sm text-primary" : "text-gray-500 hover:text-gray-700 dark:text-gray-400"}`}
              title="Grid View"
            >
              <DynamicIcon name="LayoutGrid" className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`p-2 rounded-md transition-all ${viewMode === "list" ? "bg-white dark:bg-gray-600 shadow-sm text-primary" : "text-gray-500 hover:text-gray-700 dark:text-gray-400"}`}
              title="List View"
            >
              <DynamicIcon name="List" className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      {tab === "plans" && (
        <>
          {/* GRID VIEW */}
          <div className={`${viewMode === "list" ? "hidden md:hidden" : "grid"} grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6`}>
            {plans.map((p) => (
              <div key={p.id}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-0 hover:shadow-xl transition-all border border-gray-100 dark:border-gray-700 flex flex-col overflow-hidden group"
              >
                {/* Header Gradient */}
                <div className="h-2 bg-gradient-to-r from-blue-500 to-purple-600" />

                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center gap-2">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white leading-tight">
                        {p.name}
                      </h3>
                    </div>
                    <div className="text-right leading-tight">
                      <span className="text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                        ${p.price}
                      </span>
                      <span className="text-xs text-gray-500 font-medium ml-1">/mo</span>
                    </div>
                  </div>



                  <div className="space-y-4 mb-6">
                    {/* Quotas */}
                    <div className="flex flex-row items-center justify-between text-sm font-medium bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                      <span className="flex items-center gap-1"><DynamicIcon name="Coins" className="w-4 h-4 text-yellow-500" />{p.monthly_credits?.toLocaleString()} Credits</span>
                      <span className="text-gray-300 dark:text-gray-600">|</span>
                      <span className="flex items-center gap-1"><DynamicIcon name="Users" className="w-4 h-4 text-purple-500" />{p.max_users ?? '∞'} Users</span>
                    </div>

                    <div className="border-t border-gray-100 dark:border-gray-700 my-4" />

                    {/* Features List */}
                    <div>
                      <h4 className="text-xs font-bold uppercase text-gray-400 mb-2">Included Features</h4>
                      <ul className="space-y-2">
                        {(p.features?.components || []).slice(0, 5).map((feat, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300">
                            <DynamicIcon name="CheckCircle" className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                            <span>{feat}</span>
                          </li>
                        ))}
                        {(!p.features?.components || p.features.components.length === 0) && (
                          <li className="text-sm text-gray-400 italic">No specific features listed</li>
                        )}
                        {(p.features?.components?.length > 5) && (
                          <li className="text-xs text-blue-500 pl-6">+ {p.features.components.length - 5} more...</li>
                        )}
                      </ul>
                    </div>
                  </div>

                  <div className="mt-auto pt-4 border-t border-dashed border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-500">Support</span>
                      <span className="font-semibold text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 px-2 py-1 rounded">
                        {p.features?.support || "Standard"}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(p.features?.environments || []).map(env => (
                        <span key={env} className="text-[10px] uppercase font-bold text-gray-500 border border-gray-200 dark:border-gray-600 px-1.5 rounded">
                          {env}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Admin Actions (Hidden from main card, but available via Edit) */}
                  <div className="mt-4 pt-3 border-t border-dashed border-gray-200 dark:border-gray-700 flex justify-between items-center">
                    <div className="flex gap-1">
                      <button
                        onClick={() => { setEditingPlan(p); setPlanModal(true); }}
                        className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors"
                        title="Edit Plan"
                      >
                        <DynamicIcon name="Edit" className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeletePlan(p)}
                        className="p-1.5 rounded-full hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 transition-colors"
                        title="Delete Plan"
                      >
                        <DynamicIcon name="Trash2" className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="text-right">
                      <span className="text-[10px] text-gray-400 block uppercase font-bold">Support Tier</span>
                      <span className="text-xs font-bold text-indigo-600">
                        {p.features?.support || "Standard"}
                      </span>
                    </div>
                  </div>

                </div>
              </div>
            ))}
          </div>

          {/* LIST VIEW */}
          <div className={`${viewMode === "grid" ? "hidden" : "block hidden md:block"} bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden`}>
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Plan Details</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Resource Limits</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Features</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Support</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {plans.map((p) => (
                  <tr key={p.id}>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="ml-0">
                          <div className="text-sm font-bold text-gray-900 dark:text-white">{p.name}</div>
                          <div className="text-sm text-primary font-semibold">${p.price}/mo</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex flex-col gap-1">
                        <span className="flex items-center gap-1"><DynamicIcon name="Coins" className="w-3 h-3" /> {p.monthly_credits?.toLocaleString()} credits/mo</span>
                        <span className="flex items-center gap-1"><DynamicIcon name="Users" className="w-3 h-3" /> {p.max_users ?? '∞'} users</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {(p.features?.components || []).map((feat, i) => (
                          <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                            {feat}
                          </span>
                        ))}
                        {(!p.features?.components || p.features.components.length === 0) && (
                          <span className="text-xs italic text-gray-400">None</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200">
                        {p.features?.support || "Standard"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => { setEditingPlan(p); setPlanModal(true); }}
                          className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors"
                          title="Edit Plan"
                        >
                          <DynamicIcon name="Edit" className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeletePlan(p)}
                          className="p-1.5 rounded-full hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 transition-colors"
                          title="Delete Plan"
                        >
                          <DynamicIcon name="Trash2" className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "features" && <FeaturesTab token={token} />}
      {tab === "support" && <SupportTiersTab token={token} />}

      <Modal
        isOpen={planModal}
        onClose={() => { setPlanModal(false); setEditingPlan(null); }}
        title={editingPlan ? "Edit Plan" : "New Plan"}
      >
        <PlanForm
          plan={editingPlan}
          onSave={handleSavePlan}
          onCancel={() => { setPlanModal(false); setEditingPlan(null); }}
          availableFeatures={availableFeatures}
        />
      </Modal>

      <ConfirmModal
        isOpen={!!deletePlan}
        title={`Delete ${deletePlan?.name}?`}
        onClose={() => setDeletePlan(null)}
        onConfirm={() => handleDeletePlan(deletePlan)}
      >
        Are you sure you want to delete this plan?
      </ConfirmModal>
    </div>
  );
};

export default PlansPage;
